import { cn } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/**
 * Props for the {@link SkipLink} molecule.
 *
 * @property targetId - The `id` of the landmark to jump to, usually the shell's
 *   `<main>`. Rendered as the anchor's `#`-fragment target.
 * @property label - Visible/announced text; defaults to `'Skip to content'`.
 * @property className - Extra Tailwind classes merged onto the anchor.
 *
 * @example
 * ```tsx
 * <SkipLink targetId={mainId} />
 * <main id={mainId}>…</main>
 * ```
 */
export interface SkipLinkProps {
	targetId: string
	label?: string
	className?: string
}

/**
 * A keyboard-first "skip to content" link for a shell.
 *
 * @remarks
 * - **Visually hidden until focused:** `sr-only` keeps it out of the visual flow,
 *   and `focus:not-sr-only` reveals it as a pinned chip the moment it takes focus —
 *   so it is the first Tab stop a keyboard user reaches and can use to bypass the
 *   chrome (ux-interaction-standards §10).
 * - **Real anchor to a landmark:** it is an `<a href="#targetId">` pointing at the
 *   shell's `<main>`, which is the element screen readers and browsers move focus to.
 * - **Shared across shells:** every shell (PlaneShellFrame, AuthShell, …) renders this
 *   one component so the skip-link markup and behaviour stay identical.
 * - **Styling:** every colour, radius and spacing class is a `ds-*` token utility.
 *
 * @see packages/ui-components/src/skip-link/skip-link.stories.tsx for visual examples
 */
export function SkipLink({ targetId, label = 'Skip to content', className }: SkipLinkProps): ReactElement {
	return (
		<a
			href={`#${targetId}`}
			className={cn(
				'sr-only rounded-ds-md bg-ds-primary px-3 py-2 text-ds-on-primary focus:not-sr-only focus:absolute focus:left-ds-3 focus:top-ds-3 focus:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring',
				className,
			)}
		>
			{label}
		</a>
	)
}
